import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Menu, Phone, X } from "lucide-react";
import { optimizedImages } from "@/data/optimized-images";
import { nav, resort } from "@/data/resort";
import { OptimizedImage } from "./OptimizedImage";
import { ResortLink } from "./resort-button";
import { cn } from "@/lib/utils";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isHome = pathname === "/";
  const isReservations = pathname === "/reservations" || pathname === "/reservations/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    const frame = requestAnimationFrame(onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const light = !scrolled && !menuOpen;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-500",
        scrolled || menuOpen
          ? "border-b border-border/70 bg-background/85 backdrop-blur-md"
          : "border-b border-transparent",
      )}
    >
      <div className="shell flex h-[72px] items-center justify-between gap-6 md:h-[84px]">
        <a
          href={isHome ? "#top" : "/"}
          className="flex min-w-0 shrink-0 items-center transition-colors"
          aria-label="Auburn RV Resort home"
        >
          <OptimizedImage
            image={optimizedImages.logo}
            alt="Auburn RV Resort"
            width={250}
            height={150}
            decoding="async"
            className={cn(
              "h-11 w-auto transition duration-300 md:h-14",
              light && "brightness-0 invert",
            )}
          />
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-6 xl:gap-8 lg:flex">
          {nav.map((item) => {
            const active =
              pathname === item.href || pathname === item.href.replace(/\/$/, "");
            return (
              <a
                key={item.label}
                href={item.href}
                className={cn(
                  "eyebrow link-underline transition-colors",
                  light
                    ? "text-primary-foreground/90 hover:text-primary-foreground"
                    : active
                      ? "text-foreground"
                      : "text-foreground/75 hover:text-foreground",
                )}
                aria-current={active ? "page" : undefined}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={resort.phoneHref}
            aria-label={`Call ${resort.name} at ${resort.phone}`}
            className={cn(
              "hidden size-11 items-center justify-center rounded-full border transition-colors sm:flex",
              light
                ? "border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-forest-deep"
                : "border-border text-foreground hover:bg-foreground hover:text-background",
            )}
          >
            <Phone className="size-4" strokeWidth={1.6} />
          </a>
          {!isReservations ? (
            <ResortLink
              variant={light ? "gold" : "solid"}
              href="/reservations/"
              className="hidden px-6 py-3.5 sm:inline-flex"
            >
              Check Availability
            </ResortLink>
          ) : null}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className={cn(
              "flex size-11 items-center justify-center rounded-full border transition-colors lg:hidden",
              light
                ? "border-primary-foreground/40 text-primary-foreground"
                : "border-border text-foreground",
            )}
          >
            {menuOpen ? (
              <X className="size-5" strokeWidth={1.6} />
            ) : (
              <Menu className="size-5" strokeWidth={1.6} />
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="animate-fade-in fixed inset-0 top-[72px] z-40 flex flex-col justify-between bg-background px-6 pb-10 pt-8 lg:hidden">
          <nav aria-label="Mobile" className="flex flex-col">
            {nav.map((item, i) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="border-b border-border/70 py-5 font-display text-3xl tracking-tight text-foreground"
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="space-y-3 pt-8">
            <ResortLink
              variant="solid"
              href="/reservations/"
              className="w-full"
              onClick={() => setMenuOpen(false)}
            >
              Check Availability
            </ResortLink>
            <ResortLink variant="outline" href={resort.phoneHref} className="w-full">
              <Phone className="size-4" strokeWidth={1.6} /> {resort.phone}
            </ResortLink>
          </div>
        </div>
      )}
    </header>
  );
}
